import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { PhotoDetail } from '@/types/photo';
import { getPhotoById, getPhotosByAlbumId, updatePhotoCaption, deletePhoto, downloadPhoto, setPhotoFavorite } from '@/services/photoService';
import { getAlbumById, getAllAlbums, updateAlbum, type AlbumData } from '@/services/albumService';
import PhotoViewer from '@/components/photo/PhotoViewer';
import PhotoInfoPanel from '@/components/photo/PhotoInfoPanel';
import PhotoDownloadButton from '@/components/photo/PhotoDownloadButton';

import { readAlbumSortMode, sortAlbums as sortAlbumsForNavigation } from '@/services/albumSort';

function sortPhotosForViewer(photos: PhotoDetail[]): PhotoDetail[] {
  return [...photos].sort((a, b) => {
    const dateCompare = b.shotDate.localeCompare(a.shotDate);
    if (dateCompare !== 0) return dateCompare;
    return b.id.localeCompare(a.id, undefined, { numeric: true });
  });
}

function findAdjacentAlbum(
  albums: AlbumData[],
  currentAlbumId: string | undefined,
  direction: -1 | 1,
): AlbumData | null {
  if (!currentAlbumId) return null;
  const currentIndex = albums.findIndex((item) => item.id === currentAlbumId);
  if (currentIndex < 0) return null;

  for (let index = currentIndex + direction; index >= 0 && index < albums.length; index += direction) {
    if ((albums[index].photoCount ?? 0) > 0) return albums[index];
  }
  return null;
}


export default function PhotoDetailPage() {
  const { albumId, photoId } = useParams<{ albumId: string; photoId: string }>();
  const navigate = useNavigate();

  const [photo, setPhoto] = useState<PhotoDetail | null>(null);
  const [album, setAlbum] = useState<AlbumData | null>(null);
  const [photosInAlbum, setPhotosInAlbum] = useState<PhotoDetail[]>([]);
  const [orderedAlbums, setOrderedAlbums] = useState<AlbumData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInfo, setShowInfo] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [videoError, setVideoError] = useState('');
  const [videoRetryKey, setVideoRetryKey] = useState(0);
  const [standaloneVideoUrl, setStandaloneVideoUrl] = useState('');
  const [preparingStandaloneVideo, setPreparingStandaloneVideo] = useState(false);
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  const videoPlaybackUrl = photo?.mediaType === 'video'
    ? `${photo.originalUrl}${photo.originalUrl.includes('?') ? '&' : '?'}iosRetry=${videoRetryKey}`
    : '';
  const effectiveVideoUrl = standaloneVideoUrl || videoPlaybackUrl;
  const [updatingFavorite, setUpdatingFavorite] = useState(false);
  const [movingBetweenAlbums, setMovingBetweenAlbums] = useState(false);

  const currentIndex = photosInAlbum.findIndex((p) => p.id === photoId);
  const previousAlbum = findAdjacentAlbum(orderedAlbums, albumId, -1);
  const nextAlbum = findAdjacentAlbum(orderedAlbums, albumId, 1);
  const hasPrev = currentIndex > 0 || Boolean(previousAlbum);
  const hasNext = (currentIndex >= 0 && currentIndex < photosInAlbum.length - 1) || Boolean(nextAlbum);

  // Load data
  useEffect(() => {
    if (!albumId || !photoId) {
      navigate('/albums');
      return;
    }

    const load = async () => {
      setLoading(true);
      setError('');

      const [albumData, photos, photoData, allAlbums] = await Promise.all([
        getAlbumById(albumId),
        getPhotosByAlbumId(albumId),
        getPhotoById(photoId),
        getAllAlbums(),
      ]);

      if (!albumData) {
        setError('앨범을 찾을 수 없습니다');
        setLoading(false);
        return;
      }
      setAlbum(albumData);
      setPhotosInAlbum(sortPhotosForViewer(photos));
      setOrderedAlbums(sortAlbumsForNavigation(allAlbums, readAlbumSortMode()));

      if (!photoData) {
        setError('사진을 찾을 수 없습니다');
        setLoading(false);
        return;
      }
      setPhoto(photoData);
      setVideoError('');
      setLoading(false);
    };

    load();
  }, [albumId, photoId, navigate]);

  // Cleanup
  useEffect(() => {
    return () => { /* cleanup handled by components */ };
  }, []);

  const goToPhoto = useCallback((newAlbumId: string, newPhotoId: string) => {
    navigate(`/albums/${newAlbumId}/photos/${newPhotoId}`, { replace: true });
  }, [navigate]);

  const moveToAdjacentAlbum = useCallback(async (direction: -1 | 1) => {
    if (!albumId || movingBetweenAlbums) return;
    const currentAlbumIndex = orderedAlbums.findIndex((item) => item.id === albumId);
    if (currentAlbumIndex < 0) return;

    setMovingBetweenAlbums(true);
    try {
      for (
        let index = currentAlbumIndex + direction;
        index >= 0 && index < orderedAlbums.length;
        index += direction
      ) {
        const candidate = orderedAlbums[index];
        if ((candidate.photoCount ?? 0) <= 0) continue;

        const candidatePhotos = sortPhotosForViewer(await getPhotosByAlbumId(candidate.id));
        if (candidatePhotos.length === 0) continue;

        const targetPhoto = direction === 1
          ? candidatePhotos[0]
          : candidatePhotos[candidatePhotos.length - 1];
        goToPhoto(candidate.id, targetPhoto.id);
        return;
      }
    } finally {
      setMovingBetweenAlbums(false);
    }
  }, [albumId, movingBetweenAlbums, orderedAlbums, goToPhoto]);

  const handlePrev = useCallback(() => {
    if (movingBetweenAlbums) return;
    if (currentIndex > 0) {
      goToPhoto(albumId!, photosInAlbum[currentIndex - 1].id);
      return;
    }
    void moveToAdjacentAlbum(-1);
  }, [movingBetweenAlbums, currentIndex, albumId, photosInAlbum, goToPhoto, moveToAdjacentAlbum]);

  const handleNext = useCallback(() => {
    if (movingBetweenAlbums) return;
    if (currentIndex >= 0 && currentIndex < photosInAlbum.length - 1) {
      goToPhoto(albumId!, photosInAlbum[currentIndex + 1].id);
      return;
    }
    void moveToAdjacentAlbum(1);
  }, [movingBetweenAlbums, currentIndex, albumId, photosInAlbum, goToPhoto, moveToAdjacentAlbum]);

  const handleClose = useCallback(() => {
    navigate(`/album/${albumId}`);
  }, [albumId, navigate]);


  const isStandaloneMode = useCallback(() => {
    const navigatorWithStandalone = window.navigator as Navigator & { standalone?: boolean };
    return window.matchMedia('(display-mode: standalone)').matches || navigatorWithStandalone.standalone === true;
  }, []);

  const prepareStandaloneVideo = useCallback(async () => {
    if (!photo || photo.mediaType !== 'video' || preparingStandaloneVideo) return;
    setPreparingStandaloneVideo(true);
    try {
      const response = await fetch(videoPlaybackUrl, { credentials: 'include', cache: 'no-store' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      if (!blob.size) throw new Error('empty video');
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      const objectUrl = URL.createObjectURL(blob);
      objectUrlRef.current = objectUrl;
      setStandaloneVideoUrl(objectUrl);
      setVideoError('');
    } catch {
      setVideoError('홈 화면 앱에서 동영상을 준비하지 못했습니다. Safari에서 다시 열거나 원본을 내려받아 확인해 주세요.');
    } finally {
      setPreparingStandaloneVideo(false);
    }
  }, [photo, preparingStandaloneVideo, videoPlaybackUrl]);

  useEffect(() => {
    setStandaloneVideoUrl('');
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, [photoId]);

  useEffect(() => () => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
  }, []);

  const handleMediaTouchStart = useCallback((event: React.TouchEvent) => {
    if (event.touches.length !== 1) return;
    const touch = event.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY, time: Date.now() };
  }, []);

  const handleMediaTouchEnd = useCallback((event: React.TouchEvent) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start || event.changedTouches.length !== 1 || movingBetweenAlbums) return;
    const touch = event.changedTouches[0];
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    const elapsed = Date.now() - start.time;
    if (elapsed > 900 || Math.abs(dx) < 65 || Math.abs(dx) < Math.abs(dy) * 1.25) return;
    if (dx > 0 && hasPrev) handlePrev();
    if (dx < 0 && hasNext) handleNext();
  }, [movingBetweenAlbums, hasPrev, hasNext, handlePrev, handleNext]);

  useEffect(() => {
    if (photo?.mediaType !== 'video') return;
    const handleVideoKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleClose();
      if (event.key === 'ArrowLeft' && hasPrev) handlePrev();
      if (event.key === 'ArrowRight' && hasNext) handleNext();
    };
    window.addEventListener('keydown', handleVideoKeyDown);
    return () => window.removeEventListener('keydown', handleVideoKeyDown);
  }, [photo?.mediaType, handleClose, hasPrev, hasNext, handlePrev, handleNext]);

  const handleCaptionSave = useCallback(async (caption: string): Promise<boolean> => {
    if (!photoId) return false;
    const updated = await updatePhotoCaption(photoId, caption);
    if (updated) {
      setPhoto(updated);
      setPhotosInAlbum((prev) =>
        prev.map((p) => (p.id === photoId ? updated : p)),
      );
      return true;
    }
    return false;
  }, [photoId]);

  const handleDelete = useCallback(async () => {
    if (!photoId || !albumId) return;
    const ok = await deletePhoto(photoId);
    if (ok) {
      if (album) {
        await updateAlbum(albumId, { photoCount: Math.max(0, album.photoCount - 1) });
      }
      navigate(`/album/${albumId}`);
    }
  }, [photoId, albumId, album, navigate]);

  const handleDownload = useCallback(async () => {
    if (!photoId) return;

    try {
      const result = await downloadPhoto(photoId);
      if (!result) {
        return;
      }

      const url = URL.createObjectURL(result.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      // ignore download errors
    }
  }, [photoId]);

  const handleFavorite = useCallback(async () => {
    if (!photo || updatingFavorite) return;
    setUpdatingFavorite(true);
    try {
      const updated = await setPhotoFavorite(photo.id, !photo.isFavorite);
      if (!updated) return;
      setPhoto(updated);
      setPhotosInAlbum((current) => current.map((item) => (item.id === updated.id ? updated : item)));
    } finally {
      setUpdatingFavorite(false);
    }
  }, [photo, updatingFavorite]);

  // Loading state
  if (loading) {
    return (
      <div className="fixed inset-0 z-40 bg-foreground-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-background-50/30 border-t-background-50 rounded-full animate-spin"></div>
          <p className="text-background-50/60 text-sm">사진을 불러오는 중...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !photo) {
    return (
      <div className="fixed inset-0 z-40 bg-foreground-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 px-6 text-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-50/10">
            <i className="ri-image-line text-2xl text-background-50/40"></i>
          </div>
          <p className="text-background-50/70 text-sm">{error || '사진을 불러올 수 없습니다'}</p>
          <button
            onClick={() => navigate(`/album/${albumId}`)}
            className="mt-2 px-5 py-2.5 bg-background-50/15 text-background-50 rounded-full text-sm font-medium hover:bg-background-50/25 transition-colors cursor-pointer whitespace-nowrap"
          >
            앨범으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-40 bg-foreground-950 flex flex-col">
      {photo.mediaType === 'video' ? (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-foreground-950 px-2 py-16 touch-pan-y"
          onTouchStart={handleMediaTouchStart}
          onTouchEnd={handleMediaTouchEnd}
        >
          {hasPrev && !movingBetweenAlbums && (
            <button
              type="button"
              onClick={handlePrev}
              className="absolute left-2 top-1/2 z-50 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-sm md:left-4"
              aria-label={currentIndex === 0 && previousAlbum ? `이전 앨범 ${previousAlbum.title}로 이동` : '이전 사진'}
            >
              <i className="ri-arrow-left-s-line text-3xl" />
            </button>
          )}
          {hasNext && !movingBetweenAlbums && (
            <button
              type="button"
              onClick={handleNext}
              className="absolute right-2 top-1/2 z-50 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-sm md:right-4"
              aria-label={currentIndex === photosInAlbum.length - 1 && nextAlbum ? `다음 앨범 ${nextAlbum.title}로 이동` : '다음 사진'}
            >
              <i className="ri-arrow-right-s-line text-3xl" />
            </button>
          )}
          {preparingStandaloneVideo ? (
            <div className="flex flex-col items-center gap-3 text-center text-white/80">
              <span className="h-9 w-9 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              <p className="text-sm">홈 화면 앱용 동영상을 준비하는 중...</p>
            </div>
          ) : videoError ? (
            <div className="flex max-w-sm flex-col items-center px-6 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/10">
                <i className="ri-video-line text-2xl text-white/60" />
              </div>
              <p className="mt-4 text-sm leading-6 text-white/75">{videoError}</p>
              <div className="mt-5 flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setVideoError('');
                    setStandaloneVideoUrl('');
                    setVideoRetryKey((value) => value + 1);
                  }}
                  className="rounded-full bg-white/15 px-4 py-2.5 text-sm font-medium text-white"
                >
                  다시 재생
                </button>
                <button
                  type="button"
                  onClick={handleDownload}
                  className="rounded-full bg-accent-500 px-4 py-2.5 text-sm font-medium text-white"
                >
                  원본 다운로드
                </button>
              </div>
            </div>
          ) : (
            <video
              key={`${photo.id}-${videoRetryKey}`}
              src={effectiveVideoUrl}
              poster={photo.image}
              controls
              playsInline
              preload="metadata"
              onLoadedMetadata={() => setVideoError('')}
              onCanPlay={() => setVideoError('')}
              onError={(event) => {
                if (isStandaloneMode() && !standaloneVideoUrl && !preparingStandaloneVideo) {
                  void prepareStandaloneVideo();
                  return;
                }
                const code = event.currentTarget.error?.code;
                const message = code === 2
                  ? '동영상 연결이 중단되었습니다. 다시 재생을 눌러 주세요.'
                  : code === 3
                    ? '동영상 형식을 해석하지 못했습니다. 재생용 파일을 다시 생성해야 합니다.'
                    : code === 4
                      ? '이 기기에서 지원하지 않는 동영상 형식입니다.'
                      : '동영상을 재생하지 못했습니다. 잠시 후 다시 시도해 주세요.';
                setVideoError(message);
              }}
              className="max-h-full max-w-full rounded-lg bg-black object-contain"
            >
              이 브라우저에서는 동영상 재생을 지원하지 않습니다.
            </video>
          )}
        </div>
      ) : (
        <PhotoViewer
          src={photo.image}
          alt={photo.caption || '사진'}
          onNavigatePrev={() => handlePrev()}
          onNavigateNext={() => handleNext()}
          onClose={handleClose}
          hasPrev={hasPrev && !movingBetweenAlbums}
          hasNext={hasNext && !movingBetweenAlbums}
        />
      )}


      {movingBetweenAlbums && (
        <div className="absolute inset-0 z-[60] flex items-center justify-center bg-black/35 backdrop-blur-[1px]">
          <div className="flex items-center gap-3 rounded-full bg-black/70 px-5 py-3 text-sm font-medium text-white shadow-lg">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/35 border-t-white" />
            {currentIndex === photosInAlbum.length - 1 ? '다음 앨범을 불러오는 중...' : '이전 앨범을 불러오는 중...'}
          </div>
        </div>
      )}

      {/* Top Bar */}
      <div className="absolute top-0 left-0 right-0 z-50 pt-3 px-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <button
              onClick={handleClose}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-foreground-900/50 backdrop-blur-sm text-background-50 hover:bg-foreground-900/70 transition-colors cursor-pointer flex-shrink-0"
              aria-label="뒤로 가기"
            >
              <i className="ri-arrow-left-line text-lg"></i>
            </button>
            {album && (
              <p className="text-background-50/80 text-sm font-medium truncate">{album.title}</p>
            )}
          </div>

          <div className="flex items-center gap-1.5 flex-shrink-0">
            {/* Download */}
            <button
              onClick={handleDownload}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-foreground-900/50 backdrop-blur-sm text-background-50 hover:bg-foreground-900/70 transition-colors cursor-pointer"
              aria-label="원본 다운로드"
            >
              <i className="ri-download-line text-lg"></i>
            </button>

            <button
              onClick={() => void handleFavorite()}
              disabled={updatingFavorite}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-foreground-900/50 backdrop-blur-sm text-background-50 hover:bg-foreground-900/70 transition-colors cursor-pointer disabled:opacity-60"
              aria-label={photo.isFavorite ? '즐겨찾기 해제' : '즐겨찾기 추가'}
            >
              <i className={`${photo.isFavorite ? 'ri-heart-fill text-red-300' : 'ri-heart-line'} text-lg`}></i>
            </button>

            {/* More Menu */}
            <div className="relative">
              <button
                onClick={() => setShowMoreMenu(!showMoreMenu)}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-foreground-900/50 backdrop-blur-sm text-background-50 hover:bg-foreground-900/70 transition-colors cursor-pointer"
                aria-label="더보기"
              >
                <i className="ri-more-line text-lg"></i>
              </button>

              {showMoreMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowMoreMenu(false)}></div>
                  <div className="absolute right-0 top-full mt-2 w-48 bg-background-50 rounded-xl shadow-lg border border-background-200/50 py-1 z-50">
                    <button
                      onClick={() => { setShowMoreMenu(false); setShowInfo(true); }}
                      className="w-full px-4 py-2.5 text-sm text-foreground-700 hover:bg-secondary-50 transition-colors flex items-center gap-2 cursor-pointer whitespace-nowrap"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-information-line text-sm"></i>
                      </div>
                      사진 정보
                    </button>
                    <button
                      onClick={() => {
                        setShowMoreMenu(false);
                        setShowInfo(true);
                        // Delay to let the panel mount, then trigger edit
                        setTimeout(() => {
                          const editBtn = document.querySelector('[data-photo-info-panel] [aria-label="설명 수정"]') as HTMLElement;
                          editBtn?.click();
                        }, 100);
                      }}
                      className="w-full px-4 py-2.5 text-sm text-foreground-700 hover:bg-secondary-50 transition-colors flex items-center gap-2 cursor-pointer whitespace-nowrap"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-edit-line text-sm"></i>
                      </div>
                      설명 수정
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom: Download + counter */}
      <div className="absolute bottom-0 left-0 right-0 z-40 pb-3 px-4">
        <PhotoDownloadButton photoId={photoId!} />
        <div className="flex justify-center mt-2">
          <span className="text-background-50/40 text-xs">
            {currentIndex + 1} / {photosInAlbum.length}
            {currentIndex === photosInAlbum.length - 1 && nextAlbum ? ` · 다음 앨범: ${nextAlbum.title}` : ''}
          </span>
        </div>
      </div>

      {/* Photo Info Panel (slide up) */}
      {showInfo && (
        <>
          <div className="fixed inset-0 z-50 bg-black/60" onClick={() => setShowInfo(false)}></div>
          <div className="fixed bottom-0 left-0 right-0 z-50 max-h-[70vh] overflow-y-auto" data-photo-info-panel>
            <PhotoInfoPanel
              photo={photo}
              onCaptionSave={handleCaptionSave}
              onDelete={handleDelete}
            />
          </div>
        </>
      )}
    </div>
  );
}
